import { memo, useState, useEffect, useMemo } from 'react';

import Filters from '../components/Dashboard/Filters';
import TopDataTableWrapper from '../components/Dashboard/TopDataTableWrapper';
import Charts from '../components/Dashboard/Charts';

import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';

import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { setAnalyticsState, setError } from '../redux/actions';

import { getTopDocuments, getTopChannels, getTopUsers, getDailyResults, getPublishedDocuments } from '../utils/api';
import { useDateFilters } from '../utils/hooks';
import { months, skeletonRows, topFilesColumns, channelsColumns, topUsersColumns } from '../utils/utils';

import { FiltersType, DocumentType, TopFiles, TopChannels, TopUsers, DailyResults } from '../types/AppTypes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    table: {
      margin: `${theme.spacing(2)}px auto`,
    },
    tableHeader: {
      textAlign: 'center',
    },
  }),
);

const Dashboard = (): JSX.Element => {
  const classes = useStyles();
  const [start, end] = useDateFilters();

  const filters: FiltersType = useAppSelector((state) => state.filters.filters);

  const topFiles: TopFiles[] = useAppSelector((state) => state.analytics.topFiles);
  const topChannels: TopChannels[] = useAppSelector((state) => state.analytics.topChannels);
  const topUsers: TopUsers[] = useAppSelector((state) => state.analytics.topUsers);
  const dailyResults: DailyResults[] = useAppSelector((state) => state.analytics.dailyResults);
  const documents: DocumentType[] = useAppSelector((state) => state.analytics.publishedDocuments);

  const [loading, setLoading] = useState(true);

  const dispatch = useAppDispatch();

  const documentChartData: number[] = useMemo(() => {
    const selectedMonths = months.slice(start.getMonth(), end.getMonth() + 1);
    let dataPerMonth = Array.from({ length: end.getMonth() + 1 - start.getMonth() }, (_, i) => start.getMonth() + i).reduce(
      (a, item) => ({ ...a, [item]: 0 }),
      {},
    );
    if (documents && documents.length > 0) {
      documents.map((d) => {
        if (d.created) {
          const month = new Date(d.created).getMonth();
          if (selectedMonths.includes(months[month])) {
            dataPerMonth = { ...dataPerMonth, [month]: dataPerMonth[month] + 1 };
          }
        }
        return d;
      });
    }
    return Object.values(dataPerMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, filters.start, filters.end]);

  const uniqueUserChartData: number[] = useMemo(() => {
    const selectedMonths = months.slice(start.getMonth(), end.getMonth() + 1);
    let dataPerMonth = Array.from({ length: end.getMonth() + 1 - start.getMonth() }, (_, i) => start.getMonth() + i).reduce(
      (a, item) => ({ ...a, [item]: 0 }),
      {},
    );

    if (dailyResults.length > 0) {
      dailyResults.map((d) => {
        const month = new Date(d.day).getMonth();
        if (selectedMonths.includes(months[month])) {
          dataPerMonth = { ...dataPerMonth, [month]: (dataPerMonth[month] += parseInt(d.uniqueUsers)) };
        }
        return d
      });
    }
    return Object.values(dataPerMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyResults, filters]);

  const fetchAnalyticsData = () => {
    setLoading(true);
    const topDocumentsPromise = getTopDocuments(filters.start, filters.end, filters.channels, filters.subchannels);
    const topChannelsPromise = getTopChannels(filters.start, filters.end, filters.channels, filters.subchannels);
    const topUsersPromise = getTopUsers(filters.start, filters.end, filters.channels, filters.subchannels);
    const dailyResultsPromise = getDailyResults(filters.start, filters.end, filters.channels, filters.subchannels);
    const publishedDocumentsPromise = getPublishedDocuments(
      filters.start,
      filters.end,
      filters.channels,
      filters.subchannels,
    );
    Promise.all([topDocumentsPromise, topChannelsPromise, topUsersPromise, dailyResultsPromise, publishedDocumentsPromise])
      .then((res) => {
        const payload = {
          topFiles: res[0].results,
          topChannels: res[1].results,
          topUsers: res[2].results,
          dailyResults: res[3].results,
          publishedDocuments: res[4].results,
        };
        dispatch(setAnalyticsState(payload));
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        dispatch(
          setError({
            hasError: true,
            errorMessage:
              'There is a problem in fetching the analytics report. Please check your internet connection or contact support.',
          }),
        );
      });
  };

  useEffect(() => {
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <div className={classes.root}>
      <Filters />
      <Grid container justifyContent="center" style={{ marginTop: 20 }}>
        <Charts
          months={months.slice(start.getMonth(), end.getMonth() + 1)}
          loading={!documents || documents.length === 0 || loading}
          documentData={documentChartData}
          userData={uniqueUserChartData}
        />
        <Grid container>
          <TopDataTableWrapper
            data={topFiles}
            title="Top Files"
            skeletonRows={skeletonRows}
            tableHeaderClass={classes.tableHeader}
            headerData={topFilesColumns}
            loading={loading}
            tableClass={classes.table}
          />
          <TopDataTableWrapper
            data={topChannels}
            title="Channels"
            skeletonRows={skeletonRows}
            tableHeaderClass={classes.tableHeader}
            headerData={channelsColumns}
            loading={loading}
            tableClass={classes.table}
          />
          <TopDataTableWrapper
            data={topUsers}
            title="Top Users"
            skeletonRows={skeletonRows}
            tableHeaderClass={classes.tableHeader}
            headerData={topUsersColumns}
            loading={loading}
            tableClass={classes.table}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default memo(Dashboard);
